import React, { useState } from "react";
import {
  Box,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Checkbox,
  Grid,
  Card,
  CardMedia,
  CardContent,
  Typography,
  Button,
  IconButton,
  Menu,
  MenuItem,
} from "@mui/material";
import { InsertDriveFile as FileIcon } from "@mui/icons-material";
import {
  PictureAsPdf as PdfIcon,
  MoreVert as MoreVertIcon,
} from "@mui/icons-material";
import FilePreview from "../classification_component/FilePreview";

const FileList = ({
  files = [],
  selectedFileIds = [],
  handleFileSelection,
  selectAll,
  onSelectAll,
  viewSize,
  getFilePreviewUrl,
  disabled = false,
  onDelete, // NEW PROP
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [menuFileId, setMenuFileId] = useState(null);

  const handleMenuOpen = (event, fileId) => {
    setAnchorEl(event.currentTarget);
    setMenuFileId(fileId);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setMenuFileId(null);
  };

  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewFile, setPreviewFile] = useState(null);

  const handlePreview = () => {
    const file = files.find((f) => f.id === menuFileId);
    if (file) {
      setPreviewFile(file);
      setPreviewOpen(true);
    }
    handleMenuClose();
  };

  const handleDelete = () => {
    const file = files.find((f) => f.id === menuFileId);
    handleMenuClose();
    if (file && onDelete) {
      onDelete(file);
    }
  };

  const handlePreviewClose = () => {
    setPreviewOpen(false);
    setPreviewFile(null);
  };

  if (!files || files.length === 0) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          height: "100%",
          gap: 2,
        }}
      >
        <Typography variant="body1" color="textSecondary">
          No files selected.
        </Typography>
      </Box>
    );
  }

  const isPdf = (file) => {
    return (
      file.type === "application/pdf" ||
      (file.name && file.name.toLowerCase().endsWith(".pdf"))
    );
  };

  return (
    <Box
      sx={{
        flexGrow: 1,
        overflow: "auto",
        paddingTop: 2,
        paddingLeft: 3,
        paddingRight: 3,
        display: "flex",
        flexDirection: "column",
        gap: 2,
      }}
    >
      {viewSize === "small" ? (
        <List>
          {files.map((file, index) => (
            <ListItem
              key={file.id}
              sx={{
                border: "1px solid #e0e0e0",
                borderRadius: 1,
                mb: 1,
                bgcolor: selectedFileIds.includes(file.id)
                  ? "rgba(25, 118, 210, 0.08)"
                  : "transparent",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Checkbox
                  checked={selectedFileIds.includes(file.id)}
                  onChange={(e) => {
                    console.log("Checkbox clicked for file ID:", file.id);
                    handleFileSelection(file.id);
                  }}
                />
                <ListItemIcon>
                  {file.type && file.type.startsWith("image/") ? (
                    <Box
                      component="img"
                      src={getFilePreviewUrl(file)}
                      sx={{
                        width: 40,
                        height: 40,
                        objectFit: "contain",
                        bgcolor: "#f5f5f5",
                      }}
                    />
                  ) : isPdf(file) ? (
                    <PdfIcon sx={{ fontSize: 40, color: "#757575" }} />
                  ) : (
                    <FileIcon />
                  )}
                </ListItemIcon>
                <ListItemText
                  primary={file.name}
                  secondary={`${(file.size / 1024).toFixed(2)} KB`}
                  sx={{ ml: 1 }}
                />
              </Box>
              <IconButton
                aria-label="more"
                aria-controls="long-menu"
                aria-haspopup="true"
                onClick={(e) => handleMenuOpen(e, file.id)}
                size="small"
                disabled={disabled}
              >
                <MoreVertIcon />
              </IconButton>
            </ListItem>
          ))}
        </List>
      ) : (
        <Grid container spacing={0}>
          {files.map((file, index) => (
            <Grid
              item
              xs={viewSize === "medium" ? 2 : 3}
              key={file.id}
              sx={{ paddingLeft: 2, paddingRight: 2 }}
            >
              <Card
                variant="outlined"
                sx={{
                  height: viewSize === "medium" ? 270 : 315,
                  width: "100%",
                  border: selectedFileIds.includes(file.id)
                    ? "2px solid #1976d2"
                    : "1px solid #e0e0e0",
                  margin: 0,
                  mb: 1.5,
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "space-between",
                }}
              >
                <Box sx={{ position: "relative" }}>
                  <Checkbox
                    checked={selectedFileIds.includes(file.id)}
                    onChange={(e) => handleFileSelection(file.id)}
                    sx={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      zIndex: 1,
                      bgcolor: "rgba(255,255,255,0.7)",
                      borderRadius: "50%",
                      m: 0,
                    }}
                  />
                  {file.type && file.type.startsWith("image/") ? (
                    <CardMedia
                      component="img"
                      height={viewSize === "medium" ? 210 : 260}
                      image={getFilePreviewUrl(file)}
                      alt={file.name}
                      sx={{ objectFit: "contain", bgcolor: "#f5f5f5" }}
                    />
                  ) : isPdf(file) ? (
                    <Box
                      sx={{
                        height: viewSize === "medium" ? 210 : 260,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        bgcolor: "#f5f5f5",
                      }}
                    >
                      <PdfIcon
                        sx={{
                          fontSize: viewSize === "medium" ? 80 : 100,
                          color: "#757575",
                        }}
                      />
                    </Box>
                  ) : (
                    <Box
                      sx={{
                        height: viewSize === "medium" ? 130 : 200,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        bgcolor: "#f5f5f5",
                      }}
                    >
                      <FileIcon
                        sx={{
                          fontSize: viewSize === "medium" ? 60 : 80,
                          color: "#757575",
                        }}
                      />
                    </Box>
                  )}
                  <IconButton
                    aria-label="more"
                    aria-controls="long-menu"
                    aria-haspopup="true"
                    onClick={(e) => handleMenuOpen(e, file.id)}
                    size="small"
                    sx={{ position: "absolute", top: 8, right: 8, zIndex: 2 }}
                  >
                    <MoreVertIcon />
                  </IconButton>
                </Box>
                <CardContent sx={{ py: 1, px: 0.5 }}>
                  <Typography variant="body2" noWrap title={file.name}>
                    {file.name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {(file.size / 1024).toFixed(2)} KB
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      <Menu
        id="long-menu"
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        PaperProps={{
          style: {
            maxHeight: 48 * 4.5,
            width: "20ch",
          },
        }}
      >
        <MenuItem onClick={handlePreview}>Preview</MenuItem>
        <MenuItem onClick={handleDelete}>Delete</MenuItem>
      </Menu>

      {previewFile && (
        <FilePreview
          open={previewOpen}
          onClose={handlePreviewClose}
          file={previewFile}
        />
      )}
    </Box>
  );
};

export default FileList;
